import React, { useState } from "react";
import { Container, Row, Col, Modal, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { ArrowRight, User, X } from "lucide-react";
import SearchWithAPI from "../common/searchbox/searchbox.js";
import { submitHandoverApi } from "./handover-api";
import HandoverList from "./HandoverList.jsx";
import HandoverHistory from "./HandoverHistory.jsx";
import HandoverHistoryView from "./HandoverHistoryView.jsx";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./Handover.css";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + (parts[parts.length - 1][0] || "")).toUpperCase();
};

const getUserDisplayName = (userOpt) => {
  if (!userOpt) return "";
  if (userOpt.name) return userOpt.name;
  if (userOpt.label && userOpt.label.includes("-")) {
    const parts = userOpt.label.split("-");
    if (parts[0]) return parts[0].trim();
  }
  return userOpt.label || "";
};

const getUserDisplayEmail = (userOpt) => {
  if (!userOpt) return "";
  if (userOpt.email) return userOpt.email;
  if (userOpt.label && userOpt.label.includes("-")) {
    const parts = userOpt.label.split("-");
    if (parts[1] && parts[1].includes("@")) return parts[1].trim();
  }
  if (userOpt.label && userOpt.label.includes("@")) return userOpt.label;
  return "";
};

const Handover = () => {
  // Main Tab: 'handover' or 'history'
  const [activeMainTab, setActiveMainTab] = useState("handover");
  const [selectedHistoryLog, setSelectedHistoryLog] = useState(null);

  // Handover Form State
  const [fromUser, setFromUser] = useState("");
  const [fromUserOption, setFromUserOption] = useState(null);
  const [toUser, setToUser] = useState("");
  const [toUserOption, setToUserOption] = useState(null);
  const [checkAssigned, setCheckAssigned] = useState(true);
  const [checkReporter, setCheckReporter] = useState(true);
  const [checkCreated, setCheckCreated] = useState(true);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);

  // Date & Filter Drawer State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expStartDate, setExpStartDate] = useState("");
  const [expEndDate, setExpEndDate] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProjectOptions, setSelectedProjectOptions] = useState([]);
  const [selectedTeamOptions, setSelectedTeamOptions] = useState([]);

  // Confirmation Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Reset Filters & Form
  const handleReset = () => {
    setFromUser("");
    setFromUserOption(null);
    setToUser("");
    setToUserOption(null);
    setCheckAssigned(true);
    setCheckReporter(true);
    setCheckCreated(true);
    setStartDate("");
    setEndDate("");
    setExpStartDate("");
    setExpEndDate("");
    setSelectedTicketIds([]);
    setShowSnackbar(false);
  };

  // Handle Checkbox Selection
  const handleSelectTicket = (id) => {
    if (selectedTicketIds.includes(id)) {
      setSelectedTicketIds(selectedTicketIds.filter((tId) => tId !== id));
    } else {
      setSelectedTicketIds([...selectedTicketIds, id]);
    }
  };

  const handleSelectAll = (e, tickets = []) => {
    if (e.target.checked) {
      setSelectedTicketIds(tickets.map((t) => t.id));
    } else {
      setSelectedTicketIds([]);
    }
  };

  // Open Handover Confirmation Modal
  const handleOpenSubmitModal = () => {
    if (!fromUser) {
      setSnackbarMessage("Please select a From User.");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (!toUser) {
      setSnackbarMessage("Please select a target To User.");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (fromUser === toUser) {
      setSnackbarMessage("From User and To User cannot be the same person.");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (!checkAssigned && !checkReporter && !checkCreated) {
      setSnackbarMessage("Please select at least one role criteria (Assigned, Reporter, or Created).");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (selectedTicketIds.length === 0) {
      setSnackbarMessage("Please select at least one ticket to handover.");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    setRemarks("");
    setShowConfirmModal(true);
  };

  // Submit Handover API Execution
  const handleConfirmSubmit = async () => {
    if (!remarks || remarks.trim().length < 3) {
      setSnackbarMessage("Remarks are required (minimum 3 characters).");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        from_user_id: fromUser || null,
        to_user_id: toUser || null,
        ticket_ids: selectedTicketIds,
        check_assigned_to: checkAssigned,
        check_reporter: checkReporter,
        check_created_by: checkCreated,
        remarks: remarks.trim(),
      };

      const res = await submitHandoverApi(payload);
      if (res?.data?.success) {
        setSnackbarMessage(res.data.message || "Tickets handed over successfully!");
        setSnackbarStatus(true);
        handleSnackbarShow();
        setShowConfirmModal(false);
        setSelectedTicketIds([]);
      } else {
        const errorMsg = res?.data?.error?.[0] || res?.data?.message || "Failed to handover tickets.";
        setSnackbarMessage(errorMsg);
        setSnackbarStatus(false);
        handleSnackbarShow();
      }
    } catch (err) {
      console.error("Error submitting handover:", err);
      setSnackbarMessage("An unexpected error occurred during handover.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="handover-container">
      
      {!selectedHistoryLog && (
        <>
          
          <div className="handover-header-card">
            <h2 className="handover-title">Handover</h2>
            <p className="handover-subtitle">Assign tickets from one user to another user easily.</p>
          </div>

          
          <div className="handover-filter-card">
            <div className="handover-tab-switcher">
              <div
                className="handover-tab-indicator"
                style={{
                  transform: activeMainTab === "history" ? "translateX(100%)" : "translateX(0%)",
                }}
              />
              <button
                type="button"
                className={`handover-tab-btn ${activeMainTab === "handover" ? "active" : ""}`}
                onClick={() => {
                  setActiveMainTab("handover");
                  setSelectedHistoryLog(null);
                }}
              >
                Handover
              </button>
              <button
                type="button"
                className={`handover-tab-btn ${activeMainTab === "history" ? "active" : ""}`}
                onClick={() => setActiveMainTab("history")}
              >
                History
              </button>
            </div>

            {activeMainTab === "handover" && (
              <Row className="align-items-end g-3">
                <Col lg={3} md={6}>
                  <label className="handover-filter-label">From user</label>
                  <SearchWithAPI
                    onDropdownChange={(option) => {
                      setFromUserOption(option);
                      setFromUser(option ? (option.value ?? option.id ?? "") : "");
                      setSelectedTicketIds([]);
                    }}
                    apiUrl="get_employee"
                    placeholder="Select"
                    selectedValue={fromUserOption ? fromUserOption.label : ""}
                    selectedOption={fromUserOption}
                  />
                </Col>

                <Col lg={3} md={6}>
                  <label className="handover-filter-label">To User</label>
                  <SearchWithAPI
                    onDropdownChange={(option) => {
                      const selectedVal = option ? (option.value ?? option.id ?? "") : "";
                      if (option && String(selectedVal) === String(fromUser)) {
                        setSnackbarMessage("From User and To User cannot be the same person.");
                        setSnackbarStatus(false);
                        handleSnackbarShow();
                        setToUserOption(null);
                        setToUser("");
                        return;
                      }
                      setToUserOption(option);
                      setToUser(selectedVal);
                    }}
                    apiUrl="get_employee"
                    placeholder="Select"
                    selectedValue={toUserOption ? toUserOption.label : ""}
                    selectedOption={toUserOption}
                  />
                </Col>

                <Col lg={3} md={6}>
                  <div className="handover-checkbox-group">
                    <label className="handover-checkbox-label">
                      <input
                        type="checkbox"
                        checked={checkAssigned}
                        onChange={(e) => setCheckAssigned(e.target.checked)}
                      />
                      Assigned
                    </label>
                    <label className="handover-checkbox-label">
                      <input
                        type="checkbox"
                        checked={checkReporter}
                        onChange={(e) => setCheckReporter(e.target.checked)}
                      />
                      Reporter
                    </label>
                    <label className="handover-checkbox-label">
                      <input
                        type="checkbox"
                        checked={checkCreated}
                        onChange={(e) => setCheckCreated(e.target.checked)}
                      />
                      Created
                    </label>
                  </div>
                </Col>

                <Col lg={3} md={6} className="d-flex align-items-center justify-content-end">
                  <div className="handover-vertical-divider" />
                  <div className="handover-action-buttons">
                    <button type="button" className="handover-btn-reset" onClick={handleReset}>
                      Reset
                    </button>
                    <button
                      type="button"
                      className="handover-btn-submit"
                      onClick={handleOpenSubmitModal}
                      disabled={!fromUser || !toUser || selectedTicketIds.length === 0}
                    >
                      Submit
                    </button>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </>
      )}

      
      {selectedHistoryLog ? (
        <HandoverHistoryView
          selectedHistoryLog={selectedHistoryLog}
          onBack={() => setSelectedHistoryLog(null)}
        />
      ) : activeMainTab === "handover" ? (
        <HandoverList
          fromUser={fromUser}
          checkAssigned={checkAssigned}
          checkReporter={checkReporter}
          checkCreated={checkCreated}
          selectedTicketIds={selectedTicketIds}
          onSelectTicket={handleSelectTicket}
          onSelectAll={handleSelectAll}
          onOpenFilterDrawer={() => setIsFilterOpen(true)}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          expStartDate={expStartDate}
          setExpStartDate={setExpStartDate}
          expEndDate={expEndDate}
          setExpEndDate={setExpEndDate}
          selectedProjectOptions={selectedProjectOptions}
          selectedTeamOptions={selectedTeamOptions}
          setSelectedProjectOptions={setSelectedProjectOptions}
          setSelectedTeamOptions={setSelectedTeamOptions}
        />
      ) : (
        <HandoverHistory
          onSelectLog={(log) => setSelectedHistoryLog(log)}
        />
      )}

      
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        dialogClassName="handover-confirm-modal-dialog"
      >
        <Modal.Body className="handover-modal-body">
          <div className="handover-modal-top-icon-wrapper">
            <div className="handover-modal-top-icon-inner">
              <ArrowRight size={18} />
            </div>
          </div>

          <h4 className="handover-modal-title">Are you sure want to assign these tickets?</h4>
          <p className="handover-modal-subtitle">
            Please ensure that all details are reviewed and validated carefully before proceeding with the next steps.
          </p>

          <div className="handover-flow-container">
            <div className="handover-user-card">
              <div className="handover-user-card-header from">FROM</div>
              <div className="handover-user-card-body">
                <div className="handover-avatar from">{getInitials(getUserDisplayName(fromUserOption))}</div>
                <div className="handover-user-details">
                  <span className="handover-user-name" title={getUserDisplayName(fromUserOption)}>
                    {getUserDisplayName(fromUserOption)}
                  </span>
                  <span className="handover-user-email">{getUserDisplayEmail(fromUserOption)}</span>
                </div>
              </div>
            </div>

            <div className="handover-flow-connector">
              <div className="handover-flow-line" />
              <div className="handover-flow-circle">
                <ArrowRight size={14} />
              </div>
            </div>

            <div className="handover-user-card">
              <div className="handover-user-card-header to">TO</div>
              <div className="handover-user-card-body">
                <div className="handover-avatar to">{getInitials(getUserDisplayName(toUserOption))}</div>
                <div className="handover-user-details">
                  <span className="handover-user-name" title={getUserDisplayName(toUserOption)}>
                    {getUserDisplayName(toUserOption)}
                  </span>
                  <span className="handover-user-email">{getUserDisplayEmail(toUserOption)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="handover-details-grid-box">
            <div className="handover-tickets-summary">
              <div className="handover-pill full-width">
                <span className="handover-pill-label">Total Tickets</span>
                <span className="handover-pill-value">{selectedTicketIds.length}</span>
              </div>
            </div>

            <div className="handover-roles-summary">
              <div className="handover-role-item">
                <span className="handover-role-label">ASSIGNED</span>
                <span className="handover-role-colon">:</span>
                <span className="handover-role-value">
                  <User size={14} className="handover-role-user-icon" />
                  {checkAssigned ? getUserDisplayName(toUserOption) || "-" : "-"}
                </span>
              </div>
              <div className="handover-role-item">
                <span className="handover-role-label">REPORTER</span>
                <span className="handover-role-colon">:</span>
                <span className="handover-role-value">
                  <User size={14} className="handover-role-user-icon" />
                  {checkReporter ? getUserDisplayName(toUserOption) || "-" : "-"}
                </span>
              </div>
              <div className="handover-role-item">
                <span className="handover-role-label">CREATED BY</span>
                <span className="handover-role-colon">:</span>
                <span className="handover-role-value">
                  <User size={14} className="handover-role-user-icon" />
                  {checkCreated ? getUserDisplayName(toUserOption) || "-" : "-"}
                </span>
              </div>
            </div>
          </div>

          <label className="handover-remarks-label" htmlFor="handover-remarks">
            Reason<span>*</span>
          </label>
          <textarea
            id="handover-remarks"
            className="handover-remarks-textarea"
            placeholder="Enter here..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />

          <div className="handover-modal-footer">
            <button
              type="button"
              className="handover-btn-cancel"
              onClick={() => setShowConfirmModal(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="handover-btn-confirm"
              onClick={handleConfirmSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      
      {isFilterOpen && (
        <>
          <button
            type="button"
            className="modal-overlay"
            aria-label="Close filter panel"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 2000,
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
            }}
            onClick={() => setIsFilterOpen(false)}
          />
          <div
            className="filter-drawer-panel"
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "380px",
              height: "100%",
              backgroundColor: "#F6F6F6",
              boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.15)",
              zIndex: 2001,
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflowY: "auto",
            }}
          >
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span style={{ fontSize: "18px", fontWeight: "700", color: "#212129" }}>Filters</span>
                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #EBEBEB",
                    borderRadius: "100px",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => setIsFilterOpen(false)}
                >
                  <X size={14} color="#212129" />
                </div>
              </div>

              <div style={{ borderRadius: "16px", padding: "16px", background: "#FFFFFF" }} className="d-flex flex-column gap-3">
                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Project
                  </label>
                  <SearchWithAPI
                    apiUrl="get_project"
                    placeholder="Select Project"
                    selectedValue={selectedProjectOptions && selectedProjectOptions.length > 0 ? selectedProjectOptions[0].label : ""}
                    selectedOption={selectedProjectOptions && selectedProjectOptions.length > 0 ? selectedProjectOptions[0] : null}
                    onDropdownChange={(opt) => setSelectedProjectOptions(opt ? [opt] : [])}
                  />
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Team
                  </label>
                  <SearchWithAPI
                    apiUrl="get_team"
                    placeholder="Select Team"
                    selectedValue={selectedTeamOptions && selectedTeamOptions.length > 0 ? selectedTeamOptions[0].label : ""}
                    selectedOption={selectedTeamOptions && selectedTeamOptions.length > 0 ? selectedTeamOptions[0] : null}
                    onDropdownChange={(opt) => setSelectedTeamOptions(opt ? [opt] : [])}
                  />
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Created Date Range
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Expected Date Range
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={expStartDate}
                      onChange={(e) => setExpStartDate(e.target.value)}
                    />
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={expEndDate}
                      onChange={(e) => setExpEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between gap-3 pt-3">
              <button
                type="button"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "100px",
                  backgroundColor: "#f0f0f0",
                  color: "#21272C",
                  fontWeight: "700",
                  cursor: "pointer",
                  fontSize: "13px",
                  flex: 1,
                }}
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setExpStartDate("");
                  setExpEndDate("");
                  setSelectedProjectOptions([]);
                  setSelectedTeamOptions([]);
                }}
              >
                Reset
              </button>
              <button
                type="button"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "100px",
                  backgroundColor: "#6929E2",
                  color: "#ffffff",
                  fontWeight: "700",
                  cursor: "pointer",
                  fontSize: "13px",
                  flex: 1,
                }}
                onClick={() => {
                  setIsFilterOpen(false);
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Handover;
